import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ControlledField,
  GroupLeadSchemaType,
  PhoneInputField,
  colors,
  groupLeadSchema,
} from 'remote/global';
import { Box, Stack } from '@mui/system';
import { toast } from 'react-hot-toast';
import {
  useOperatorsOrdersQuery,
  usePatchOperatorsOrdersDetailsMutation,
} from 'api/queries/operators';
import { OrderDetailsSchemaPayloadType } from './types';

function BookingEditGroupLeadForm({
  defaultValues,
  bookingNumber,
  isOrderEditAvailable,
}: {
  defaultValues?: GroupLeadSchemaType;
  bookingNumber: string;
  isOrderEditAvailable: boolean;
}) {
  const tInstance = useTranslation();
  const { data: selectedOrder } = useOperatorsOrdersQuery(bookingNumber);
  const { t } = tInstance;
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValidating, isLoading },
  } = useForm<GroupLeadSchemaType>({
    defaultValues: {
      groupLead: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      },
      ...defaultValues,
    },
    resolver: zodResolver(groupLeadSchema),
  });
  const patchOrdersDetails = usePatchOperatorsOrdersDetailsMutation(bookingNumber);

  const onValid = async (data: GroupLeadSchemaType) => {
    const payload: OrderDetailsSchemaPayloadType = {
      ...selectedOrder,
      ...data,
    } as any;

    try {
      await patchOrdersDetails.mutateAsync(payload);
    } catch (err) {
      toast.error(t('Toast.failure'));
    }
  };

  return (
    <Box sx={{ maxWidth: '350px', mx: 'auto', my: '2rem' }}>
      <Typography sx={{ color: colors.uiBlue600, mb: '12px', fontWeight: 600, fontSize: '18px' }}>
        {t('terms.groupLeadDetails.title')}
      </Typography>
      <form onSubmit={handleSubmit(onValid)}>
        <Stack direction='column' spacing={1}>
          <ControlledField
            control={control}
            name='groupLead.firstName'
            translationKey='firstName'
            fieldProps={{
              InputProps: { autoComplete: 'given-name', disabled: !isOrderEditAvailable },
            }}
          />
          <ControlledField
            control={control}
            name='groupLead.lastName'
            translationKey='lastName'
            fieldProps={{
              InputProps: { autoComplete: 'family-name', disabled: !isOrderEditAvailable },
            }}
          />
          <ControlledField
            control={control}
            name='groupLead.email'
            translationKey='email'
            fieldProps={{
              InputProps: {
                type: 'email',
                autoComplete: 'email',
                disabled: !isOrderEditAvailable,
              },
            }}
          />
          <ControlledField
            control={control}
            name='groupLead.phone'
            render={(renderProps) => (
              <PhoneInputField
                {...renderProps}
                translationKey='phone'
                fieldProps={{
                  disabled: !isOrderEditAvailable,
                }}
              />
            )}
          />
        </Stack>
        <Button
          type='submit'
          disabled={!isOrderEditAvailable || isSubmitting || isValidating || isLoading}
          variant='contained'
          fullWidth
          sx={{ mt: '2rem' }}
        >
          {t('save')}
        </Button>
      </form>
    </Box>
  );
}

export default BookingEditGroupLeadForm;
